import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Loader, Sparkles, UserCircle, ExternalLink } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';
import { getPublicLandscapingQuestion } from '../services/landscapingQuestionsApi';
import ShareIdeaRow from '../components/ShareIdeaRow';
import SaveAnswerPrompt from '../components/SaveAnswerPrompt';
import ExpertNoteByline from '../components/ExpertNoteByline';
import ExpertNoteFollowUpCta from '../components/ExpertNoteFollowUpCta';
import { isLoggedInCustomer } from '../utils/customerAccount';

function usePublicUserId() {
  try {
    const raw = localStorage.getItem('userData') || localStorage.getItem('user');
    if (!raw) return null;
    return JSON.parse(raw)?.id || null;
  } catch {
    return null;
  }
}

export default function AskPage() {
  const { id: idParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUserId = usePublicUserId();

  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    document.title = 'AI Answer | PlantaDay';
  }, []);

  useEffect(() => {
    if (!idParam) {
      navigate('/ideas', { replace: true });
    }
  }, [idParam, navigate]);

  useEffect(() => {
    if (!idParam) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getPublicLandscapingQuestion(idParam);
        if (cancelled) return;
        setResult({
          id: data.id,
          question_text: data.question_text,
          ai_answer: data.ai_answer,
          expert_notes: data.expert_notes || [],
          created_at: data.created_at,
        });
        setQuestion(data.question_text || '');
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load this answer.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [idParam]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <PublicNav currentUserId={currentUserId} />
      <main className="flex-1 max-w-3xl mx-auto px-4 py-10 sm:py-14 w-full">
        <Link to="/ideas" className="text-sm font-medium text-green-700 hover:underline">
          ← Get new ideas
        </Link>
        <h1 className="mt-4 text-3xl font-bold text-gray-900">AI answer</h1>
        <p className="mt-2 text-gray-500 text-sm">
          Bookmark this page or keep the link you copied when you asked. If you opted in for email, we will notify you when an expert note is published.
        </p>

        {idParam && loading && !result && !error && (
          <div className="mt-10 flex justify-center text-gray-500">
            <Loader className="w-8 h-8 animate-spin" />
          </div>
        )}

        {idParam && error && (
          <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 text-sm">
            {error}{' '}
            <Link to="/ideas" className="font-medium underline">
              Ask a new question
            </Link>
          </div>
        )}

        {result && (
          <div className="mt-10 space-y-6">
            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Your question</h2>
              <p className="mt-2 text-gray-900 whitespace-pre-wrap">{result.question_text}</p>
            </div>

            <section className="rounded-xl border-2 border-emerald-200 bg-emerald-50/80 p-5 sm:p-6">
              <div className="flex items-center gap-2 text-emerald-900">
                <Sparkles className="w-5 h-5 flex-shrink-0" aria-hidden />
                <h2 className="text-lg font-bold">AI answer</h2>
              </div>
              <p className="mt-4 text-gray-800 whitespace-pre-wrap leading-relaxed">{result.ai_answer}</p>
              <p className="mt-4 text-xs text-emerald-800/90">
                Generated by AI. Verify important decisions with local pros and your own site conditions.
              </p>
              {result.id && <ShareIdeaRow questionId={result.id} />}
              {!isLoggedInCustomer() && <SaveAnswerPrompt />}
            </section>

            {result.expert_notes && result.expert_notes.length > 0 && (
              <section className="space-y-4">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Expert advice</h2>
                {result.expert_notes.map((note) => (
                  <div
                    key={note.id}
                    className="rounded-xl border-2 border-blue-200 bg-blue-50/90 p-5 sm:p-6"
                  >
                    <div className="flex items-center gap-2 text-blue-900">
                      <UserCircle className="w-5 h-5 flex-shrink-0" aria-hidden />
                      <span className="text-lg font-bold">Expert advice</span>
                      {note.contributor_label && (
                        <span className="text-sm font-medium text-blue-800">
                          (
                          <ExpertNoteByline
                            note={note}
                            className="text-sm font-medium text-blue-800"
                            inline
                          />
                          )
                        </span>
                      )}
                    </div>
                    <p className="mt-3 text-gray-900 whitespace-pre-wrap leading-relaxed">{note.advice_text}</p>
                    <ExpertNoteFollowUpCta note={note} className="text-xs text-blue-900/80 mt-3 leading-snug" />
                  </div>
                ))}
              </section>
            )}

            <div className="flex flex-wrap gap-3 pt-2">
              <Link
                to="/find-designer"
                className="inline-flex items-center gap-1 text-blue-700 font-semibold hover:underline"
              >
                Find a designer <ExternalLink className="w-4 h-4" />
              </Link>
              <Link to="/ideas" className="text-green-700 font-semibold hover:underline">
                Ask another question
              </Link>
            </div>

            {result.id && (
              <p className="text-xs text-gray-500">
                Bookmark this page to see updates if we add expert notes later.
              </p>
            )}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
